// components/Navigation.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { useCart } from "../../../context/CartContext";
import { useAuth } from "../../../context/AuthContext";
import { useCurrency } from "../../../context/CurrencyContext";
import CurrencySwitcher from "./CurrencySwitcher";
import logo from "../../../assets/BMLogo.png"

const Navigation = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [announcement, setAnnouncement] = useState({
        isActive: false,
        text: '',
        highlightText: ''
    });

    const { cart, clearCart } = useCart();
    const { currentUser, logout, userRole } = useAuth();
    const { usAvailable, currency } = useCurrency();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAnnouncement = async () => {
            try {
                const snap = await getDoc(doc(db, "site_config", "home_content"));
                if (snap.exists() && snap.data().announcementData) {
                    setAnnouncement(snap.data().announcementData);
                }
            } catch (err) {
                console.error("Failed to load announcement banner", err);
            }
        };
        fetchAnnouncement();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
        setDropdownOpen(false);
    }, [location.pathname]);

    const handleNavClick = () => {
        setIsMenuOpen(false);
        setDropdownOpen(false);
    };

    // We keep this function here just in case you ever want to call it from a dropdown in the future
    const handleLogout = async () => {
        try {
            await logout();
            clearCart();
            navigate('/');
            handleNavClick();
        } catch (error) {
            console.error("Failed to log out", error);
        }
    };

    const getCartItemsCount = () => {
        return cart.reduce((total, item) => total + item.quantity, 0);
    };

    const handleMouseEnter = () => {
        if (window.innerWidth >= 1200) setDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        if (window.innerWidth >= 1200) setDropdownOpen(false);
    };

    return (
        <>
            <style>
                {`
                .custom-dropdown-menu {
                    background-color: #0a0a0a;
                    border: 1px solid rgba(212, 175, 55, 0.3);
                    border-radius: 0;
                    margin-top: 0;
                    padding: 10px 0;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.8);
                    z-index: 1050; 
                }
                
                .dropdown-item-custom {
                    font-family: 'Montserrat', sans-serif;
                    font-size: 0.85rem;
                    padding: 12px 24px;
                    transition: all 0.2s ease;
                    white-space: normal;
                }

                .dropdown-item-custom:hover, 
                .navbar-toggler-icon {
                   filter: brightness(0) saturate(100%) invert(75%) sepia(45%) saturate(543%) hue-rotate(352deg) brightness(85%) contrast(87%);
                }
                
                .navbar-toggler:focus {
                    box-shadow: none;
                    border-color: rgba(212, 175, 55, 0.5);
                }
                .dropdown-item-custom:focus {
                    background-color: rgba(212, 175, 55, 0.1) !important;
                }

                @media (max-width: 1199.98px) {
                    .custom-dropdown-menu {
                        background-color: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        text-align: center;
                        padding-bottom: 15px;
                        z-index: auto;
                    }
                    
                    /* Centers all navigation links on mobile */
                    .navbar-nav {
                        text-align: center;
                        align-items: center;
                        width: 100%;
                        padding-top: 20px;
                    }

                    /* Gives the links some breathing room */
                    .nav-item {
                        width: 100%;
                        margin-bottom: 15px;
                    }

                    /* Fixes the Cart Badge so it anchors to the icon */
                    .cart-icon {
                        position: relative;
                        display: inline-block !important; 
                    }
                    
                    .cart-count {
                        position: absolute;
                        top: 0px;
                        right: -15px;
                        /* Ensure your gold badge styling stays intact here */
                    }
                    
                    .dropdown-item-custom {
                        padding: 10px 20px;
                    }
                }
                `}
            </style>

            <nav className={`navbar navbar-expand-xl navbar-light sticky-top ${isScrolled ? 'scrolled' : ''}`} style={{ zIndex: 1050 }}>
                <div className="container-fluid px-4 px-lg-5">
                    <Link className="navbar-brand d-flex align-items-center" to="/" onClick={handleNavClick}>
                        <img
                            src={logo}
                            alt="BM Logo"
                            style={{ height: '45px', width: 'auto' }}
                            className="me-2"
                        />
                        <span className="d-none d-md-inline ms-2">
                            Bundle Madness
                        </span>
                    </Link>

                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>

                    <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
                        <ul className="navbar-nav ms-auto">
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/" onClick={handleNavClick}>
                                    Home
                                </Link>
                            </li>

                            <li className="nav-item dropdown" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                <span
                                    className={`nav-link dropdown-toggle d-flex justify-content-center align-items-center gap-1 ${
                                        location.pathname === '/shop' || location.pathname === '/pure-madness' ? 'active' : ''
                                    }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setDropdownOpen(!dropdownOpen);
                                    }}
                                >
                                    Shop
                                </span>
                                <ul className={`dropdown-menu custom-dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                                    <li>
                                        <Link className="dropdown-item dropdown-item-custom" to="/shop" onClick={handleNavClick} style={{ color: '#f5f0e8' }}>
                                            Signature Virgin Hair
                                        </Link>
                                    </li>
                                    <li>
                                        <Link className="dropdown-item dropdown-item-custom" to="/pure-madness" onClick={handleNavClick} style={{ color: '#d4af37', fontWeight: '600' }}>
                                            Pure Madness Raw Hair
                                        </Link>
                                    </li>
                                </ul>
                            </li>

                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} to="/about" onClick={handleNavClick}>
                                    About
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/care-guide' ? 'active' : ''}`} to="/care-guide" onClick={handleNavClick}>
                                    Care Guide
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/reviews' ? 'active' : ''}`} to="/reviews" onClick={handleNavClick}>
                                    Reviews
                                </Link>
                            </li>

                            {currentUser ? (
                                <>
                                    <li className="nav-item">
                                        <Link className={`nav-link ${location.pathname === '/account' ? 'active' : ''}`} to="/account" onClick={handleNavClick}>
                                            <i className="fas fa-user me-1"></i>
                                            Account
                                        </Link>
                                    </li>

                                    {(userRole === 'admin' || currentUser.email === 'your-admin-email@gmail.com') && (
                                        <li className="nav-item">
                                            <Link className={`nav-link admin-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`} to="/admin" onClick={handleNavClick}>
                                                <i className="fas fa-cog me-1"></i>
                                                Admin
                                            </Link>
                                        </li>
                                    )}
                                    {/* The Logout button has been completely removed from this section */}
                                </>
                            ) : (
                                <li className="nav-item">
                                    <Link className={`nav-link ${location.pathname === '/login' ? 'active' : ''}`} to="/login" onClick={handleNavClick}>
                                        <i className="fas fa-user me-1"></i>
                                        Login
                                    </Link>
                                </li>
                            )}

                            {/* £ / $ toggle: only shown when US sales are switched on in the admin */}
                            {usAvailable && (
                                <li className="nav-item d-flex align-items-center justify-content-center ms-xl-3 me-xl-2">
                                    <CurrencySwitcher />
                                </li>
                            )}

                            <li className="nav-item">
                                <Link className="nav-link cart-icon" to="/cart" onClick={handleNavClick}>
                                    <i className="fas fa-shopping-cart"></i>
                                    {getCartItemsCount() > 0 && (
                                        <span className="cart-count">{getCartItemsCount()}</span>
                                    )}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            {/* US visitors only see the bar if there's a US message or a US discount (otherwise the code wouldn't work for them) */}
            {announcement.isActive && (currency !== 'USD' || announcement.textUSD || Number(announcement.discountAmountUSD) > 0) && (
                <div style={{
                    backgroundColor: "#030303",
                    color: "#f4f4f4",
                    textAlign: "center",
                    padding: "10px 15px",
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.75rem",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    borderBottom: "1px solid rgba(212, 175, 55, 0.15)",
                    position: "relative",
                    zIndex: 1030
                }}>
                    {currency === 'USD' && announcement.textUSD ? announcement.textUSD : announcement.text}
                    {announcement.highlightText && (
                        <span style={{ fontWeight: "700", color: "#d4af37", marginLeft: "4px" }}>
                            {announcement.highlightText}
                        </span>
                    )}
                </div>
            )}
        </>
    );
};

export default Navigation;